/**
 * FindingDrawerContext — a single, app-global mount point for the universal
 * finding drill-down drawer (`components/Findings/FindingDetail`).
 *
 * Why this exists: before this, the universal drawer was rendered only inside
 * FindingsPage. Every other pillar (KSPM, CWPP, SSPM, …) either rolled its own
 * thinner detail panel or had no drill-down at all. Mounting the drawer once at
 * the app root and exposing `useFindingDrawer().openFinding(f)` lets any
 * surface open the SAME rich drawer (Details / Graph / Related / History /
 * Remediation / Notes + the loop-closing Auto-Remediate / Create-Ticket
 * actions) with a single call — no per-pillar drawer boilerplate.
 *
 * The drawer's tab endpoints resolve against `csp_core.findings` by id, so a
 * caller should pass a finding whose `id` is a real csp_core.findings id. The
 * drawer degrades gracefully (per-tab "HTTP 404") when it isn't, so passing a
 * pillar-local finding never crashes — but callers should prefer the canonical
 * id when they have it.
 */
import React, { createContext, useContext, useCallback, useState } from 'react';
import FindingDetail from '../components/Findings/FindingDetail';

// Permissive finding shape: the drawer's Details tab renders straight from this
// object; only id/title/severity/status/source_module are structurally needed.
// Everything else is optional enrichment. Kept loose so heterogeneous pillar
// rows can be adapted without a brittle shared type.
export interface DrawerFinding {
  id: string;
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'informational' | string;
  status: string;
  source_module: string;
  [key: string]: unknown;
}

interface FindingDrawerContextValue {
  /** Open the universal drawer for the given finding. Replaces any open one. */
  openFinding: (finding: DrawerFinding) => void;
  /** Close the drawer. */
  closeFinding: () => void;
  /** The currently-open finding, or null. */
  current: DrawerFinding | null;
}

const FindingDrawerContext = createContext<FindingDrawerContextValue | null>(null);

export const useFindingDrawer = (): FindingDrawerContextValue => {
  const ctx = useContext(FindingDrawerContext);
  if (!ctx) {
    throw new Error('useFindingDrawer must be used within a FindingDrawerProvider');
  }
  return ctx;
};

export const FindingDrawerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [current, setCurrent] = useState<DrawerFinding | null>(null);

  const openFinding = useCallback((finding: DrawerFinding) => {
    setCurrent(finding);
  }, []);

  const closeFinding = useCallback(() => {
    setCurrent(null);
  }, []);

  return (
    <FindingDrawerContext.Provider value={{ openFinding, closeFinding, current }}>
      {children}
      {current && (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        <FindingDetail finding={current as any} onClose={closeFinding} />
      )}
    </FindingDrawerContext.Provider>
  );
};

export default FindingDrawerProvider;
